<script setup lang="ts">
import { bemBuilder, debounce } from "@chatfood/core-utils";
import { ref, onMounted, onUnmounted, watch, nextTick } from "vue";
import type { ISatButtonCurrentLocationEmits } from "@chatfood/design-system";
import {
  AtomText,
  AtomInput,
  AtomLoadingSpinner,
  WoLocation,
  SatFormControl,
  SatButtonCurrentLocation,
} from "@chatfood/design-system";
import type { ICurbsideLocationsProps } from "./props";
import type { ICurbsideLocationsEmits } from "./emits";
import { useBusinessStore } from "~/stores/business";
import { toDeliverectChannel } from "~/utils/to-deliverect-channel";
import type { IDomainOutletState } from "~/domain/outlet-state";
import type { IDomainOutletCurbside } from "~/domain/outlet-curbside";
import { ETA_PREPARATION_MARGIN } from "~/domain/constants";
import { useLocale } from "~/composables/use-locale";
import { useDomainLabel } from "~/composables/use-domain-label";
import { usePublicConfig } from "~/config";
import { useDirectBackend } from "#imports";
import { useNavigation } from "~/composables/use-navigation";
import { useWindowResize } from "~/composables/use-window-resize";

const css = bemBuilder("curbside-locations");

const props = defineProps<ICurbsideLocationsProps>();
const emits = defineEmits<ICurbsideLocationsEmits>();
const { push } = useNavigation();

const { t } = useLocale();
const { storeRepo } = useDirectBackend();
const { outletStateMap } = useDomainLabel();
const { isTablet } = useWindowResize();

const business = useBusinessStore();

type Store = Awaited<ReturnType<typeof storeRepo.getStores>>["data"][number];

const isLoading = ref(true);
const isLoadingMore = ref(false);
const isLoadingGeolocation = ref(false);
const loadingCurbsideIds = ref(new Set<string>());

const latitude = ref<number>();
const longitude = ref<number>();

const mapState: Record<IDomainOutletState, (typeof WoLocation)["state"]> = {
  open: "open",
  closed: "close",
  busy: "busy",
  pre_order_only: "soon",
};

async function initGeolocation() {
  isLoadingGeolocation.value = true;

  try {
    const res = await findPosition();
    if (res) setLocation(res.coords);
  } catch {
    // silent geolocation error
  } finally {
    isLoadingGeolocation.value = false;
  }
}

function findPosition(): Promise<GeolocationPosition> | undefined {
  const geolocation = window.navigator?.geolocation;

  if (!geolocation) return;

  return new Promise((resolve, reject) => {
    geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
    });
  });
}

function setLocation(coords: Partial<GeolocationCoordinates>) {
  if (
    coords.latitude == null ||
    coords.longitude == null ||
    (latitude.value === coords.latitude && longitude.value === coords.longitude)
  ) {
    return;
  }

  latitude.value = coords.latitude;
  longitude.value = coords.longitude;

  emits("geoLocationObtained", {
    lat: coords.latitude,
    lng: coords.longitude,
  });

  getLocations(true);
}

const currentPage = ref(1);
const hasMorePages = ref(false);
const LOCATIONS_PER_PAGE = 20;

const locations = ref<IDomainOutletCurbside[]>([]);
const loadMoreRef = ref<HTMLElement>();
const observer = ref<IntersectionObserver>();

const getLocations = async (reset = false) => {
  if (reset) {
    isLoading.value = true;
    currentPage.value = 1;
    locations.value = [];
    hasMorePages.value = false;
  } else {
    isLoadingMore.value = true;
  }

  try {
    const res = await storeRepo.getStores({
      accountId: business.accountId,
      latitude: latitude.value?.toString(),
      longitude: longitude.value?.toString(),
      ...(search.value && { name: search.value }),
      page: currentPage.value,
      fulfillmentType: ["curbside"],
      channels: [toDeliverectChannel(business.salesChannel)],
      size: LOCATIONS_PER_PAGE,
    });

    const curbsideStores = res.data.map<IDomainOutletCurbside>(
      (store: Store) => ({
        id: store.id,
        name: store.name,
        location: store.address?.location,
        city: store.address?.city,
        coordinates: store.address?.coordinate,
        distance: store.distance ?? undefined,
        state: store.status,
        preparationMinutes:
          store.settings.curbside.scheduling.preparationMinutes,
      }),
    );

    locations.value = reset
      ? curbsideStores
      : [...locations.value, ...curbsideStores];
    hasMorePages.value = locations.value.length < (res.meta?.to ?? 0);
    emits("loadedLocations", locations.value);
  } finally {
    isLoading.value = false;
    isLoadingMore.value = false;
    if (reset) resetObserver();
  }
};

const loadMoreLocations = async () => {
  if (!hasMorePages.value || isLoadingMore.value) return;

  currentPage.value += 1;
  await getLocations(false);
};

const onIntersect = (entries: IntersectionObserverEntry[]) => {
  if (entries[0].isIntersecting && hasMorePages.value && !isLoadingMore.value) {
    loadMoreLocations();
  }
};

const setupIntersectionObserver = () => {
  if (!loadMoreRef.value || observer.value) return;
  observer.value = new IntersectionObserver(onIntersect, {
    rootMargin: "100px",
    threshold: 0.1,
  });
  observer.value.observe(loadMoreRef.value);
};

const cleanupObserver = () => {
  observer.value?.disconnect();
  observer.value = undefined;
};

const resetObserver = () => {
  cleanupObserver();
  nextTick(setupIntersectionObserver);
};

onMounted(() => {
  Promise.all([initGeolocation(), getLocations(true)]).then(() => {
    nextTick(() => {
      setupIntersectionObserver();
    });
  });
});

onUnmounted(() => {
  cleanupObserver();
});

const onClickLocation = (location: IDomainOutletCurbside) => {
  emits("clickedLocation", location);
};

const onCurbsideAt = async (storeId: string) => {
  loadingCurbsideIds.value.add(storeId);

  try {
    await push("outlet", {
      orderingMode: "curbside",
      outletSlug: storeId,
    });
  } catch {
    // silent error
  } finally {
    loadingCurbsideIds.value.delete(storeId);
  }
};

const focusedLocation = ref<IDomainOutletCurbside>();
function onFocusLocation(location: IDomainOutletCurbside) {
  focusedLocation.value = location;
  emits("focusedLocation", focusedLocation.value);
}

const onSelectLocationInfo = (locationId: IDomainOutletCurbside["id"]) => {
  emits("selectedLocationId", locationId);
};

function onBlurLocation() {
  focusedLocation.value = undefined;
}

const distanceFormatted = (distance?: number) => {
  if (typeof distance !== "number") return undefined;

  const distanceKm = distance / 1000;

  return distanceKm < 1
    ? t("components.curbside_locations.x_meters", {
        distance: Math.floor(distance),
      })
    : t("components.curbside_locations.x_kilometers", {
        distance: Math.floor(distanceKm),
      });
};

const { googleMapsApiKey } = usePublicConfig();

const onCurrentLocation = (
  location: ISatButtonCurrentLocationEmits["success"][0],
) => {
  setLocation({
    latitude: location.coordinates.lat,
    longitude: location.coordinates.lng,
  });
};

const search = ref("");
const debouncedGetLocations = debounce(() => getLocations(true), 500);

watch(
  [() => business.salesChannel, search],
  ([newChannel, newSearch], [oldChannel, oldSearch]) => {
    if (newChannel !== oldChannel || newSearch !== oldSearch) {
      debouncedGetLocations();
    }
  },
);

watch([() => locations.value.length, hasMorePages], () => {
  nextTick(() => {
    if (loadMoreRef.value && hasMorePages.value && !observer.value) {
      setupIntersectionObserver();
    }
  });
});

watch(
  () => props.currentGeoLocation,
  (newCoordinate) => {
    if (newCoordinate) {
      setLocation({
        latitude: newCoordinate.lat,
        longitude: newCoordinate.lng,
      });
    }
  },
);
</script>

<template>
  <div :class="[css(''), business.isMobileApp && css('', 'mobile-app')]">
    <div>
      <SatFormControl
        data-test="search"
        :class="css('search')"
        :label="t('components.curbside_locations.search_label')"
      >
        <div :class="css('search-content')">
          <div :class="css('search-input')">
            <AtomInput
              autofocus
              :value="search"
              :placeholder="
                t('components.curbside_locations.search_placeholder')
              "
              @keyup="(value) => (search = value)"
            />
          </div>
          <SatButtonCurrentLocation
            :class="css('current-location-cta')"
            :api-key="googleMapsApiKey"
            :country="business.country"
            :is-loading="isLoadingGeolocation"
            @success="onCurrentLocation"
            @click="emits('recenterMap')"
          />
        </div>
      </SatFormControl>
    </div>

    <div v-if="isLoading" :class="css('loading')">
      <AtomLoadingSpinner />
    </div>
    <div v-else :class="css('content')">
      <WoLocation
        v-for="(item, index) in locations"
        :key="item.id"
        :class="css('location')"
        :name="item.name"
        :address="item.location ?? ''"
        :address-prefix="distanceFormatted(item.distance)"
        :badge-value="`${index + 1}`"
        :state="mapState[item.state]"
        :state-label="outletStateMap[item.state]"
        :primary-button-label-loading="loadingCurbsideIds.has(item.id)"
        :state-description="
          item.state === 'open' || item.state === 'busy'
            ? t('components.curbside_locations.preparation', {
                prepStart: item.preparationMinutes,
                prepEnd: item.preparationMinutes + ETA_PREPARATION_MARGIN,
              })
            : ''
        "
        :is-active="props.clickedLocationId === item.id"
        :primary-button-label="t('components.curbside_locations.order_cta')"
        :warn-label="
          item.state === 'busy'
            ? t('components.curbside_locations.busy_warning')
            : ''
        "
        :is-busy="item.state === 'busy'"
        :is-primary-button-disabled="item.state === 'closed'"
        :secondary-button-label="
          isTablet ? '' : t('components.curbside_locations.info_cta')
        "
        @click-primary="onCurbsideAt(item.id)"
        @click-secondary="onSelectLocationInfo(item.id)"
        @mouse-hover="onFocusLocation(item)"
        @mouse-out="onBlurLocation"
        @click="onClickLocation(item)"
      />
      <AtomText v-if="!locations?.length" color="color-text-secondary">
        {{ t("components.curbside_locations.empty_message") }}
      </AtomText>
      <div
        v-if="hasMorePages && locations.length > 0"
        ref="loadMoreRef"
        data-test="load-more"
        :class="css('load-more')"
      >
        <AtomLoadingSpinner v-if="isLoadingMore" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.curbside-locations {
  display: flex;
  gap: var(--spacing-md);
  flex-direction: column;

  &__content {
    min-height: 104px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: var(--spacing-sm);
  }

  &__loading {
    height: 104px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__search-content {
    display: flex;
    align-items: center;
    gap: var(--spacing-md);
    position: relative;
  }

  &__search-input {
    flex: 1;
  }

  &__location {
    width: 100%;
    padding-inline: 0;
  }

  &__current-location-cta {
    position: absolute;
    top: 50%;
    right: 16px;
    transform: translateY(-50%);
  }

  &__load-more {
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: var(--spacing-sm);
  }

  &--mobile-app .curbside-locations__location {
    padding-inline: var(--spacing-md);
  }
}
</style>
